import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useSettings } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  ArrowLeft,
  Palette,
  Settings,
  Upload,
  Save,
  RotateCcw,
  Eye,
  Shield,
  Globe,
  Mail,
  Phone
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminSettings = () => {
  const { user } = useAuth();
  const { platformSettings, updateSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [formData, setFormData] = useState({
    platform_name: '',
    logo_url: '',
    favicon_url: '',
    
    // Homepage content
    hero_title: '',
    hero_subtitle: '',
    hero_description: '',
    hero_text: '',
    restriction_warning: '',
    restriction_text: '',
    background_image_url: '',
    
    // Colors
    primary_color: '#3b82f6',
    secondary_color: '#8b5cf6',
    accent_color: '#10b981',
    background_color: '#0f172a',
    text_color: '#ffffff',
    hero_background_color: '',
    
    // Contact
    footer_text: '',
    contact_email: '',
    support_phone: ''
  });

  useEffect(() => {
    if (platformSettings) {
      setFormData({
        platform_name: platformSettings.platform_name || '',
        logo_url: platformSettings.logo_url || '',
        favicon_url: platformSettings.favicon_url || '',
        
        // Homepage content
        hero_title: platformSettings.hero_title || 'DIGITAL TV PLAY',
        hero_subtitle: platformSettings.hero_subtitle || 'Curta os principais canais',
        hero_description: platformSettings.hero_description || 'com os melhores conteúdos',
        hero_text: platformSettings.hero_text || 'Na Digital TV Play você tem acesso à melhor programação ao vivo com qualidade premium.',
        restriction_warning: platformSettings.restriction_warning || '⚠️ Plataforma de Acesso Restrito',
        restriction_text: platformSettings.restriction_text || 'O cadastro requer aprovação administrativa para garantir qualidade e segurança.',
        background_image_url: platformSettings.background_image_url || '',
        
        // Colors
        primary_color: platformSettings.primary_color || '#3b82f6',
        secondary_color: platformSettings.secondary_color || '#8b5cf6',
        accent_color: platformSettings.accent_color || '#10b981',
        background_color: platformSettings.background_color || '#0f172a',
        text_color: platformSettings.text_color || '#ffffff',
        hero_background_color: platformSettings.hero_background_color || '',
        
        // Contact
        footer_text: platformSettings.footer_text || '',
        contact_email: platformSettings.contact_email || '',
        support_phone: platformSettings.support_phone || ''
      });
    }
  }, [platformSettings]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Apply preview changes in real-time for colors
    if (field.includes('color') && previewMode) {
      const root = document.documentElement;
      if (root) {
        root.style.setProperty(`--${field.replace('_', '-')}`, value);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(`${API}/superadmin/settings/platform`, formData);
      updateSettings(response.data);
      toast.success('Configurações atualizadas com sucesso!');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Erro ao atualizar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewToggle = () => {
    if (previewMode) {
      // Reset to original colors
      if (platformSettings) {
        updateSettings(platformSettings);
      }
    } else {
      // Apply preview colors
      const root = document.documentElement;
      if (root) {
        Object.keys(formData).forEach(key => {
          if (key.includes('color')) {
            root.style.setProperty(`--${key.replace('_', '-')}`, formData[key]);
          }
        });
      }
      
      // Update page title for preview
      if (formData.platform_name && document) {
        document.title = formData.platform_name;
      }
    }
    setPreviewMode(!previewMode);
  };

  const resetToDefaults = () => {
    setFormData({
      platform_name: 'Digital TV Play',
      logo_url: '',
      favicon_url: '',
      
      // Homepage content
      hero_title: 'DIGITAL TV PLAY',
      hero_subtitle: 'Curta os principais canais',
      hero_description: 'com os melhores conteúdos',
      hero_text: 'Na Digital TV Play você tem acesso à melhor programação ao vivo com qualidade premium.',
      restriction_warning: '⚠️ Plataforma de Acesso Restrito',
      restriction_text: 'O cadastro requer aprovação administrativa para garantir qualidade e segurança.',
      background_image_url: '',
      
      // Colors
      primary_color: '#3b82f6',
      secondary_color: '#8b5cf6',
      accent_color: '#10b981',
      background_color: '#0f172a',
      text_color: '#ffffff',
      hero_background_color: '',
      
      // Contact
      footer_text: '',
      contact_email: '',
      support_phone: ''
    });
  };

  const colorPresets = [
    {
      name: 'Azul Padrão',
      colors: {
        primary_color: '#3b82f6',
        secondary_color: '#8b5cf6',
        accent_color: '#10b981',
        background_color: '#0f172a',
        text_color: '#ffffff'
      }
    },
    {
      name: 'Verde Esmeralda',
      colors: {
        primary_color: '#059669',
        secondary_color: '#10b981',
        accent_color: '#34d399',
        background_color: '#064e3b',
        text_color: '#ffffff'
      }
    },
    {
      name: 'Roxo Elegante',
      colors: {
        primary_color: '#7c3aed',
        secondary_color: '#a855f7',
        accent_color: '#c084fc',
        background_color: '#2e1065',
        text_color: '#ffffff'
      }
    },
    {
      name: 'Laranja Vibrante',
      colors: {
        primary_color: '#ea580c',
        secondary_color: '#f97316',
        accent_color: '#fb923c',
        background_color: '#7c2d12',
        text_color: '#ffffff'
      }
    }
  ];

  const applyColorPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      ...preset.colors
    }));
    
    if (previewMode) {
      const root = document.documentElement;
      if (root) {
        Object.keys(preset.colors).forEach(key => {
          root.style.setProperty(`--${key.replace('_', '-')}`, preset.colors[key]);
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin">
              <Button variant="outline" size="sm" className="text-white border-white/20">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white flex items-center">
              <Shield className="mr-3 h-6 w-6 text-red-400" />
              Configurações da Plataforma
            </h1>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              onClick={handlePreviewToggle}
              variant="outline"
              size="sm"
              className={`text-white border-white/20 ${previewMode ? 'bg-blue-600' : ''}`}
            >
              <Eye className="h-4 w-4 mr-2" />
              {previewMode ? 'Sair da Prévia' : 'Prévia'}
            </Button>
            <span className="text-white text-sm">SuperAdmin: {user?.full_name}</span>
          </div>
        </div>
      </header>

      {/* Preview Mode Banner */}
      {previewMode && (
        <div className="bg-blue-600 text-white p-2 text-center">
          <span className="font-semibold">Modo Prévia Ativo</span> - As mudanças são temporárias
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          <Tabs defaultValue="branding" className="space-y-6">
            <TabsList className="bg-white/10 border-white/20">
              <TabsTrigger value="branding" className="text-white">
                <Globe className="h-4 w-4 mr-2" />
                Marca e Logo
              </TabsTrigger>
              <TabsTrigger value="content" className="text-white">
                <Settings className="h-4 w-4 mr-2" />
                Conteúdo da Página
              </TabsTrigger>
              <TabsTrigger value="colors" className="text-white">
                <Palette className="h-4 w-4 mr-2" />
                Cores e Tema
              </TabsTrigger>
              <TabsTrigger value="contact" className="text-white">
                <Mail className="h-4 w-4 mr-2" />
                Contato
              </TabsTrigger>
            </TabsList>

            {/* Branding Tab */}
            <TabsContent value="branding">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Identidade da Plataforma</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="platform_name" className="text-white">Nome da Plataforma *</Label>
                      <Input
                        id="platform_name"
                        value={formData.platform_name}
                        onChange={(e) => handleInputChange('platform_name', e.target.value)}
                        className="bg-white/5 border-white/20 text-white"
                        placeholder="Digite o nome da plataforma"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="logo_url" className="text-white">URL do Logo</Label>
                      <Input
                        id="logo_url"
                        type="url"
                        value={formData.logo_url}
                        onChange={(e) => handleInputChange('logo_url', e.target.value)}
                        className="bg-white/5 border-white/20 text-white"
                        placeholder="https://exemplo.com/logo.png"
                      />
                      <p className="text-xs text-gray-400">
                        Recomendado: PNG/SVG, máximo 200x80px, fundo transparente
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="favicon_url" className="text-white">URL do Favicon</Label>
                      <Input
                        id="favicon_url"
                        type="url"
                        value={formData.favicon_url}
                        onChange={(e) => handleInputChange('favicon_url', e.target.value)}
                        className="bg-white/5 border-white/20 text-white"
                        placeholder="https://exemplo.com/favicon.ico"
                      />
                      <p className="text-xs text-gray-400">
                        Recomendado: ICO ou PNG, 32x32px
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Prévia da Marca</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-black/20 rounded-lg p-6 text-center">
                      {formData.logo_url ? (
                        <img 
                          src={formData.logo_url} 
                          alt="Logo Preview"
                          className="max-h-16 mx-auto mb-4"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                          <Globe className="h-8 w-8 text-white" />
                        </div>
                      )}
                      <h3 className="text-2xl font-bold text-white mb-2">
                        {formData.platform_name || 'Nome da Plataforma'}
                      </h3>
                      <p className="text-gray-300 text-sm">
                        Streaming de TV ao vivo
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Content Tab */}
            <TabsContent value="content">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Textos da Página Inicial</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="hero_title" className="text-white">Título Principal *</Label>
                      <Input
                        id="hero_title"
                        value={formData.hero_title}
                        onChange={(e) => handleInputChange('hero_title', e.target.value)}
                        className="bg-white/5 border-white/20 text-white"
                        placeholder="DIGITAL TV PLAY"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hero_subtitle" className="text-white">Subtítulo</Label>
                      <Input
                        id="hero_subtitle"
                        value={formData.hero_subtitle}
                        onChange={(e) => handleInputChange('hero_subtitle', e.target.value)}
                        className="bg-white/5 border-white/20 text-white"
                        placeholder="Curta os principais canais"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hero_description" className="text-white">Descrição</Label>
                      <Input
                        id="hero_description"
                        value={formData.hero_description}
                        onChange={(e) => handleInputChange('hero_description', e.target.value)}
                        className="bg-white/5 border-white/20 text-white"
                        placeholder="com os melhores conteúdos"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hero_text" className="text-white">Texto Principal</Label>
                      <Textarea
                        id="hero_text"
                        value={formData.hero_text}
                        onChange={(e) => handleInputChange('hero_text', e.target.value)}
                        className="bg-white/5 border-white/20 text-white"
                        placeholder="Texto descritivo da plataforma..."
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="restriction_warning" className="text-white">Aviso de Restrição</Label>
                      <Input
                        id="restriction_warning"
                        value={formData.restriction_warning}
                        onChange={(e) => handleInputChange('restriction_warning', e.target.value)}
                        className="bg-white/5 border-white/20 text-white"
                        placeholder="⚠️ Plataforma de Acesso Restrito"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="restriction_text" className="text-white">Texto do Aviso</Label>
                      <Textarea
                        id="restriction_text"
                        value={formData.restriction_text}
                        onChange={(e) => handleInputChange('restriction_text', e.target.value)}
                        className="bg-white/5 border-white/20 text-white"
                        placeholder="Explicação sobre o acesso restrito..."
                        rows={2}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Imagens e Background</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="background_image_url" className="text-white">Imagem de Fundo da Página</Label>
                      <Input
                        id="background_image_url"
                        type="url"
                        value={formData.background_image_url}
                        onChange={(e) => handleInputChange('background_image_url', e.target.value)}
                        className="bg-white/5 border-white/20 text-white"
                        placeholder="https://exemplo.com/background.jpg"
                      />
                      <p className="text-xs text-gray-400">
                        Recomendado: 1920x1080px, formato JPG/PNG
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hero_background_color" className="text-white">Cor de Fundo Alternativa</Label>
                      <div className="flex space-x-2">
                        <Input
                          id="hero_background_color"
                          type="color"
                          value={formData.hero_background_color}
                          onChange={(e) => handleInputChange('hero_background_color', e.target.value)}
                          className="w-12 h-10 p-1 bg-white/5 border-white/20"
                        />
                        <Input
                          value={formData.hero_background_color}
                          onChange={(e) => handleInputChange('hero_background_color', e.target.value)}
                          className="flex-1 bg-white/5 border-white/20 text-white"
                          placeholder="#0f172a"
                        />
                      </div>
                      <p className="text-xs text-gray-400">
                        Será usada se não houver imagem de fundo
                      </p>
                    </div>

                    {/* Preview */}
                    <div className="border border-white/20 rounded-lg p-4">
                      <h4 className="text-white font-semibold mb-3">Prévia da Página Inicial</h4>
                      <div 
                        className="relative rounded-lg p-4 min-h-[200px] bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900"
                        style={{
                          backgroundImage: formData.background_image_url ? `url(${formData.background_image_url})` : 'none',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundColor: formData.hero_background_color || formData.background_color
                        }}
                      >
                        {formData.background_image_url && (
                          <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                        )}
                        <div className="relative z-10 text-center">
                          <h1 className="text-2xl font-bold text-white mb-2">
                            {formData.hero_title || 'DIGITAL TV PLAY'}
                          </h1>
                          <p className="text-blue-200 mb-1">
                            {formData.hero_subtitle || 'Curta os principais canais'}
                          </p>
                          <p className="text-white mb-2">
                            {formData.hero_description || 'com os melhores conteúdos'}
                          </p>
                          <p className="text-gray-300 text-sm">
                            {formData.hero_text || 'Texto principal da plataforma...'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            <TabsContent value="colors">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Paleta de Cores</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="primary_color" className="text-white">Cor Primária</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="primary_color"
                            type="color"
                            value={formData.primary_color}
                            onChange={(e) => handleInputChange('primary_color', e.target.value)}
                            className="w-12 h-10 p-1 bg-white/5 border-white/20"
                          />
                          <Input
                            value={formData.primary_color}
                            onChange={(e) => handleInputChange('primary_color', e.target.value)}
                            className="flex-1 bg-white/5 border-white/20 text-white"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="secondary_color" className="text-white">Cor Secundária</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="secondary_color"
                            type="color"
                            value={formData.secondary_color}
                            onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                            className="w-12 h-10 p-1 bg-white/5 border-white/20"
                          />
                          <Input
                            value={formData.secondary_color}
                            onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                            className="flex-1 bg-white/5 border-white/20 text-white"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="accent_color" className="text-white">Cor de Destaque</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="accent_color"
                            type="color"
                            value={formData.accent_color}
                            onChange={(e) => handleInputChange('accent_color', e.target.value)}
                            className="w-12 h-10 p-1 bg-white/5 border-white/20"
                          />
                          <Input
                            value={formData.accent_color}
                            onChange={(e) => handleInputChange('accent_color', e.target.value)}
                            className="flex-1 bg-white/5 border-white/20 text-white"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="background_color" className="text-white">Cor de Fundo</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="background_color"
                            type="color"
                            value={formData.background_color}
                            onChange={(e) => handleInputChange('background_color', e.target.value)}
                            className="w-12 h-10 p-1 bg-white/5 border-white/20"
                          />
                          <Input
                            value={formData.background_color}
                            onChange={(e) => handleInputChange('background_color', e.target.value)}
                            className="flex-1 bg-white/5 border-white/20 text-white"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">Presets de Cores</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {colorPresets.map((preset) => (
                          <Button
                            key={preset.name}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => applyColorPreset(preset)}
                            className="text-white border-white/20 hover:bg-white/10 justify-start"
                          >
                            <div className="flex space-x-1 mr-2">
                              <div 
                                className="w-3 h-3 rounded"
                                style={{ backgroundColor: preset.colors.primary_color }}
                              />
                              <div 
                                className="w-3 h-3 rounded"
                                style={{ backgroundColor: preset.colors.secondary_color }}
                              />
                            </div>
                            {preset.name}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Prévia das Cores</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div 
                        className="rounded-lg p-4"
                        style={{ backgroundColor: formData.background_color }}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h4 style={{ color: formData.text_color }} className="font-semibold">
                            Exemplo de Interface
                          </h4>
                        </div>
                        
                        <div className="space-y-3">
                          <div 
                            className="px-4 py-2 rounded text-white font-medium"
                            style={{ backgroundColor: formData.primary_color }}
                          >
                            Botão Primário
                          </div>
                          
                          <div 
                            className="px-4 py-2 rounded text-white font-medium"
                            style={{ backgroundColor: formData.secondary_color }}
                          >
                            Botão Secundário
                          </div>
                          
                          <div 
                            className="px-4 py-2 rounded text-white font-medium"
                            style={{ backgroundColor: formData.accent_color }}
                          >
                            Botão de Destaque
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Contact Tab */}
            <TabsContent value="contact">
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Informações de Contato</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact_email" className="text-white">Email de Contato</Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => handleInputChange('contact_email', e.target.value)}
                        className="bg-white/5 border-white/20 text-white"
                        placeholder="contato@exemplo.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="support_phone" className="text-white">Telefone de Suporte</Label>
                      <Input
                        id="support_phone"
                        value={formData.support_phone}
                        onChange={(e) => handleInputChange('support_phone', e.target.value)}
                        className="bg-white/5 border-white/20 text-white"
                        placeholder="(11) 99999-9999"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="footer_text" className="text-white">Texto do Rodapé</Label>
                    <Textarea
                      id="footer_text"
                      value={formData.footer_text}
                      onChange={(e) => handleInputChange('footer_text', e.target.value)}
                      className="bg-white/5 border-white/20 text-white"
                      placeholder="Texto que aparecerá no rodapé da plataforma"
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-8 border-t border-white/10">
            <Button
              type="button"
              variant="outline"
              onClick={resetToDefaults}
              className="text-white border-white/20"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Restaurar Padrões
            </Button>

            <div className="flex space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={handlePreviewToggle}
                className="text-white border-white/20"
              >
                <Eye className="h-4 w-4 mr-2" />
                {previewMode ? 'Sair da Prévia' : 'Visualizar'}
              </Button>
              
              <Button
                type="submit"
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="loading-spinner mr-2" />
                    Salvando...
                  </div>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Salvar Configurações
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default SuperAdminSettings;